import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'react-toastify';
import {
  KeyIcon,
  CreditCardIcon,
  BuildingOfficeIcon,
  TruckIcon,
  ChartBarIcon,
  ClipboardDocumentIcon,
  ArrowPathIcon,
  ShieldCheckIcon,
  EyeIcon,
  EyeSlashIcon,
  EnvelopeIcon,
  CheckCircleIcon,
  XCircleIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Spinner } from '../components/ui/Spinner';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';
import { configurationService } from '../services/configuration.service';
import { WebhooksSection } from '../components/configurations';
import { useAuthStore } from '../stores';
import type { CompanyConfigurations, IntegrationProvider, EmailDomainConfig, DnsRecord } from '../types';
import {
  isGoogleAnalyticsServerSideConfigured,
  prepareGoogleAnalyticsCredentials,
} from '../utils/configuration';

// Validation schemas for each provider
const authNetSchema = z.object({
  apiLoginId: z.string().min(1, 'API Login ID is required'),
  clientKey: z.string().min(1, 'Client Key is required'),
  transactionKey: z.string().min(1, 'Transaction Key is required'),
  signatureKey: z.string().optional(),
});

const mdiSchema = z.object({
  clientId: z.string().min(1, 'Client ID is required'),
  clientSecret: z.string().min(1, 'Client Secret is required'),
});

const curexaSchema = z.object({
  clientKey: z.string().min(1, 'Client Key is required'),
  clientSecret: z.string().min(1, 'Client Secret is required'),
});

const pcpHealthSchema = z.object({
  accountId: z.string().min(1, 'Account ID is required'),
  secret: z.string().min(1, 'Secret is required'),
  subAccountId: z.string().optional(),
});

const googleAnalyticsSchema = z.object({
  measurementId: z.string().optional(),
  apiSecret: z.string().optional(),
});

type AuthNetFormData = z.infer<typeof authNetSchema>;
type MdiFormData = z.infer<typeof mdiSchema>;
type CurexaFormData = z.infer<typeof curexaSchema>;
type PcpHealthFormData = z.infer<typeof pcpHealthSchema>;
type GoogleAnalyticsFormData = z.infer<typeof googleAnalyticsSchema>;

interface IntegrationCardProps {
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  isConfigured: boolean;
  children: React.ReactNode;
  onSave: () => void;
  isSaving: boolean;
}

function IntegrationCard({
  title,
  description,
  icon: Icon,
  isConfigured,
  children,
  onSave,
  isSaving,
}: IntegrationCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <Card className="overflow-hidden">
      <div
        className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-secondary-700/50 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-4">
          <div className={`p-3 rounded-lg ${isConfigured ? 'bg-green-100 dark:bg-green-900/30' : 'bg-gray-100 dark:bg-secondary-700'}`}>
            <Icon className={`w-6 h-6 ${isConfigured ? 'text-green-600 dark:text-green-400' : 'text-gray-500 dark:text-secondary-400'}`} />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-secondary-100">{title}</h3>
            <p className="text-sm text-gray-500 dark:text-secondary-400">{description}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span
            className={`px-2.5 py-1 text-xs font-medium rounded-full ${
              isConfigured
                ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                : 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'
            }`}
          >
            {isConfigured ? 'Configured' : 'Not Configured'}
          </span>
          <svg
            className={`w-5 h-5 text-gray-400 dark:text-secondary-500 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>
      {isExpanded && (
        <div className="border-t border-gray-200 dark:border-secondary-700 p-4 bg-gray-50 dark:bg-secondary-800">
          {children}
          <div className="mt-4 flex justify-end">
            <Button onClick={onSave} isLoading={isSaving}>
              Save Changes
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}

export default function Configurations() {
  const { business } = useAuthStore();
  const [configurations, setConfigurations] = useState<CompanyConfigurations | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [savingProvider, setSavingProvider] = useState<IntegrationProvider | null>(null);
  const [showApiKey, setShowApiKey] = useState(false);
  const [newApiKey, setNewApiKey] = useState<string | null>(null);
  const [showRotateConfirm, setShowRotateConfirm] = useState(false);
  const [showRevokeConfirm, setShowRevokeConfirm] = useState(false);
  const [isApiActionLoading, setIsApiActionLoading] = useState(false);
  const [showPasswords, setShowPasswords] = useState<Record<string, boolean>>({});

  // Email domain state
  const [emailDomain, setEmailDomain] = useState<EmailDomainConfig | null>(null);
  const [domainInput, setDomainInput] = useState('');
  const [isEmailDomainLoading, setIsEmailDomainLoading] = useState(false);
  const [isEmailDomainSaving, setIsEmailDomainSaving] = useState(false);
  const [showDeleteDomainConfirm, setShowDeleteDomainConfirm] = useState(false);

  const authNetWebhookUrl = business?.id
    ? `https://api.capturehealth.io/callbacks/authorizenet/${business.id}`
    : '';

  // Forms for each provider
  const authNetForm = useForm<AuthNetFormData>({
    resolver: zodResolver(authNetSchema),
    defaultValues: { apiLoginId: '', clientKey: '', transactionKey: '', signatureKey: '' },
  });

  const mdiForm = useForm<MdiFormData>({
    resolver: zodResolver(mdiSchema),
    defaultValues: { clientId: '', clientSecret: '' },
  });

  const curexaForm = useForm<CurexaFormData>({
    resolver: zodResolver(curexaSchema),
    defaultValues: { clientKey: '', clientSecret: '' },
  });

  const pcpHealthForm = useForm<PcpHealthFormData>({
    resolver: zodResolver(pcpHealthSchema),
    defaultValues: { accountId: '', secret: '', subAccountId: '' },
  });

  const gaForm = useForm<GoogleAnalyticsFormData>({
    resolver: zodResolver(googleAnalyticsSchema),
    defaultValues: { measurementId: '', apiSecret: '' },
  });

  useEffect(() => {
    loadConfigurations();
    loadEmailDomain();
  }, []);

  const loadConfigurations = async () => {
    try {
      setIsLoading(true);
      const data = await configurationService.getConfigurations();
      setConfigurations(data);

      // Populate forms with existing data (masked values)
      if (data.integrations.authNet) {
        authNetForm.reset({
          apiLoginId: data.integrations.authNet.apiLoginId || '',
          clientKey: data.integrations.authNet.clientKey || '',
          transactionKey: data.integrations.authNet.transactionKey || '',
          signatureKey: data.integrations.authNet.signatureKey || '',
        });
      }
      if (data.integrations.mdi) {
        mdiForm.reset({
          clientId: data.integrations.mdi.clientId || '',
          clientSecret: data.integrations.mdi.clientSecret || '',
        });
      }
      if (data.integrations.curexa) {
        curexaForm.reset({
          clientKey: data.integrations.curexa.clientKey || '',
          clientSecret: data.integrations.curexa.clientSecret || '',
        });
      }
      if (data.integrations.pcpHealth) {
        pcpHealthForm.reset({
          accountId: data.integrations.pcpHealth.accountId || '',
          secret: data.integrations.pcpHealth.secret || '',
          subAccountId: data.integrations.pcpHealth.subAccountId || '',
        });
      }
      if (data.integrations.googleAnalytics) {
        gaForm.reset({
          measurementId: data.integrations.googleAnalytics.measurementId || '',
          apiSecret: data.integrations.googleAnalytics.apiSecret || '',
        });
      }
    } catch (error) {
      toast.error('Failed to load configurations');
    } finally {
      setIsLoading(false);
    }
  };

  const loadEmailDomain = async () => {
    try {
      setIsEmailDomainLoading(true);
      const data = await configurationService.getEmailDomain();
      setEmailDomain(data);
      if (data) {
        setDomainInput(data.domain);
      }
    } catch (error) {
      // No domain configured yet - this is fine
      setEmailDomain(null);
    } finally {
      setIsEmailDomainLoading(false);
    }
  };

  const handleSaveEmailDomain = async () => {
    if (!domainInput.trim()) {
      toast.error('Please enter a domain');
      return;
    }

    try {
      setIsEmailDomainSaving(true);
      const result = await configurationService.createEmailDomain(domainInput.trim());
      setEmailDomain(result);
      toast.success('Email domain configured successfully. Please add the DNS records shown below.');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to configure email domain');
    } finally {
      setIsEmailDomainSaving(false);
    }
  };

  const handleRefreshVerification = async () => {
    try {
      setIsEmailDomainLoading(true);
      const result = await configurationService.verifyEmailDomain();
      setEmailDomain(result);
      if (result.isFullyVerified) {
        toast.success('Domain is fully verified!');
      } else {
        toast.info('Verification status updated');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to refresh verification status');
    } finally {
      setIsEmailDomainLoading(false);
    }
  };

  const handleDeleteEmailDomain = async () => {
    try {
      setIsEmailDomainLoading(true);
      await configurationService.deleteEmailDomain();
      setEmailDomain(null);
      setDomainInput('');
      setShowDeleteDomainConfirm(false);
      toast.success('Email domain configuration removed');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to remove email domain');
    } finally {
      setIsEmailDomainLoading(false);
    }
  };

  const handleSaveCredentials = async (provider: IntegrationProvider, data: Record<string, unknown>) => {
    try {
      setSavingProvider(provider);
      const credentials = provider === 'googleAnalytics'
        ? prepareGoogleAnalyticsCredentials(data as Record<string, string | null | undefined>)
        : data;
      await configurationService.updateIntegrationCredentials(provider, credentials as any);
      toast.success(`${provider} credentials updated successfully`);
      await loadConfigurations();
    } catch (error) {
      toast.error(`Failed to update ${provider} credentials`);
    } finally {
      setSavingProvider(null);
    }
  };

  const handleEnableApiAccess = async () => {
    try {
      setIsApiActionLoading(true);
      const result = await configurationService.enableApiAccess();
      setNewApiKey(result.apiKey);
      toast.success('API access enabled successfully');
      await loadConfigurations();
    } catch (error) {
      toast.error('Failed to enable API access');
    } finally {
      setIsApiActionLoading(false);
    }
  };

  const handleRotateApiKey = async () => {
    try {
      setIsApiActionLoading(true);
      const result = await configurationService.rotateApiKey();
      setNewApiKey(result.apiKey);
      setShowRotateConfirm(false);
      toast.success('API key rotated successfully');
      await loadConfigurations();
    } catch (error) {
      toast.error('Failed to rotate API key');
    } finally {
      setIsApiActionLoading(false);
    }
  };

  const handleRevokeApiAccess = async () => {
    try {
      setIsApiActionLoading(true);
      await configurationService.revokeApiAccess();
      setNewApiKey(null);
      setShowRevokeConfirm(false);
      toast.success('API access revoked');
      await loadConfigurations();
    } catch (error) {
      toast.error('Failed to revoke API access');
    } finally {
      setIsApiActionLoading(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const togglePasswordVisibility = (field: string) => {
    setShowPasswords((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-secondary-50">Configurations</h1>
        <p className="text-gray-600 dark:text-secondary-400 mt-1">
          Manage your integration credentials and API access settings
        </p>
      </div>

      {/* API Access Section */}
      <Card padding="none">
        <div className="p-6 border-b border-gray-200 dark:border-secondary-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 dark:bg-primary-900/30 rounded-lg">
              <KeyIcon className="w-6 h-6 text-primary dark:text-primary-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold dark:text-secondary-100">Capture Health API Key</h2>
              <p className="text-sm text-gray-500 dark:text-secondary-400">
                Use this API key to integrate with our platform
              </p>
            </div>
          </div>
        </div>
        <div className="p-6">
          {newApiKey && (
            <div className="mb-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg">
              <div className="flex items-start gap-3">
                <ShieldCheckIcon className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-green-800 dark:text-green-300">
                    Your new API key (only shown once):
                  </p>
                  <div className="mt-2 flex items-center gap-2">
                    <code className="flex-1 p-2 bg-white dark:bg-secondary-800 rounded border border-green-200 dark:border-green-700 font-mono text-sm break-all dark:text-secondary-200">
                      {newApiKey}
                    </code>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => copyToClipboard(newApiKey)}
                    >
                      <ClipboardDocumentIcon className="w-4 h-4" />
                    </Button>
                  </div>
                  <p className="mt-2 text-xs text-green-700 dark:text-green-400">
                    Make sure to copy and store this key securely. You won't be able to see it again.
                  </p>
                </div>
              </div>
            </div>
          )}

          {configurations?.apiAccessEnabled ? (
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-secondary-300 mb-1">
                    Current API Key
                  </label>
                  <div className="flex items-center gap-2">
                    <Input
                      type={showApiKey ? 'text' : 'password'}
                      value={configurations.captureApiKey || '••••••••••••••••'}
                      readOnly
                      className="font-mono"
                    />
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setShowApiKey(!showApiKey)}
                    >
                      {showApiKey ? (
                        <EyeSlashIcon className="w-4 h-4" />
                      ) : (
                        <EyeIcon className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </div>
              </div>
              <div className="flex gap-3">
                <Button
                  variant="secondary"
                  onClick={() => setShowRotateConfirm(true)}
                  disabled={isApiActionLoading}
                >
                  <ArrowPathIcon className="w-4 h-4 mr-2" />
                  Rotate Key
                </Button>
                <Button
                  variant="danger"
                  onClick={() => setShowRevokeConfirm(true)}
                  disabled={isApiActionLoading}
                >
                  Revoke Access
                </Button>
              </div>
            </div>
          ) : (
            <div className="text-center py-6">
              <KeyIcon className="w-12 h-12 text-gray-300 dark:text-secondary-600 mx-auto mb-3" />
              <p className="text-gray-600 dark:text-secondary-400 mb-4">
                API access is not enabled for your account
              </p>
              <Button onClick={handleEnableApiAccess} isLoading={isApiActionLoading}>
                Enable API Access
              </Button>
            </div>
          )}
        </div>
      </Card>

      {/* Webhooks Section */}
      <WebhooksSection />

      {/* Email Domain Configuration Section */}
      <Card padding="none">
        <div className="p-6 border-b border-gray-200 dark:border-secondary-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 dark:bg-primary-900/30 rounded-lg">
              <EnvelopeIcon className="w-6 h-6 text-primary dark:text-primary-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold dark:text-secondary-100">Email Domain Configuration</h2>
              <p className="text-sm text-gray-500 dark:text-secondary-400">
                Send emails from your own domain instead of capturehealth.com
              </p>
            </div>
          </div>
        </div>
        <div className="p-6">
          {isEmailDomainLoading && !emailDomain ? (
            <div className="flex justify-center py-8">
              <Spinner size="md" />
            </div>
          ) : emailDomain ? (
            <div className="space-y-6">
              {/* Domain Info and Actions */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 dark:text-secondary-400">Configured Domain</p>
                  <p className="text-lg font-medium text-gray-900 dark:text-secondary-100">{emailDomain.domain}</p>
                  <p className="text-sm text-gray-500 dark:text-secondary-400 mt-1">
                    Emails will be sent from: <span className="font-mono">noreply@{emailDomain.domain}</span>
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={handleRefreshVerification}
                    disabled={isEmailDomainLoading}
                  >
                    <ArrowPathIcon className={`w-4 h-4 mr-2 ${isEmailDomainLoading ? 'animate-spin' : ''}`} />
                    Refresh Status
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => setShowDeleteDomainConfirm(true)}
                    disabled={isEmailDomainLoading}
                  >
                    <TrashIcon className="w-4 h-4 mr-2" />
                    Remove
                  </Button>
                </div>
              </div>

              {/* Status Badge and Checklist */}
              <div className="bg-gray-50 dark:bg-secondary-800 rounded-lg p-4">
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-sm font-medium text-gray-700 dark:text-secondary-300">Status:</span>
                  <span
                    className={`px-2.5 py-1 text-xs font-medium rounded-full ${
                      emailDomain.overallStatus === 'verified'
                        ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                        : emailDomain.overallStatus === 'failed'
                        ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                        : emailDomain.overallStatus === 'partial'
                        ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'
                        : 'bg-gray-100 dark:bg-secondary-700 text-gray-700 dark:text-secondary-300'
                    }`}
                  >
                    {emailDomain.overallStatus === 'verified'
                      ? 'Fully Configured'
                      : emailDomain.overallStatus === 'failed'
                      ? 'Verification Failed'
                      : emailDomain.overallStatus === 'partial'
                      ? 'Partially Verified'
                      : 'Pending Verification'}
                  </span>
                </div>
                <div className="flex items-center gap-6">
                  <div className="flex items-center gap-2">
                    {emailDomain.verificationStatus === 'verified' ? (
                      <CheckCircleIcon className="w-5 h-5 text-green-500 dark:text-green-400" />
                    ) : emailDomain.verificationStatus === 'failed' ? (
                      <XCircleIcon className="w-5 h-5 text-red-500 dark:text-red-400" />
                    ) : (
                      <div className="w-5 h-5 rounded-full border-2 border-gray-300 dark:border-secondary-600" />
                    )}
                    <span className="text-sm text-gray-600 dark:text-secondary-400">Domain Verified</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {emailDomain.mailFromStatus === 'verified' ? (
                      <CheckCircleIcon className="w-5 h-5 text-green-500 dark:text-green-400" />
                    ) : emailDomain.mailFromStatus === 'failed' ? (
                      <XCircleIcon className="w-5 h-5 text-red-500 dark:text-red-400" />
                    ) : (
                      <div className="w-5 h-5 rounded-full border-2 border-gray-300 dark:border-secondary-600" />
                    )}
                    <span className="text-sm text-gray-600 dark:text-secondary-400">MAIL FROM</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {emailDomain.dkimStatus === 'verified' ? (
                      <CheckCircleIcon className="w-5 h-5 text-green-500 dark:text-green-400" />
                    ) : emailDomain.dkimStatus === 'failed' ? (
                      <XCircleIcon className="w-5 h-5 text-red-500 dark:text-red-400" />
                    ) : (
                      <div className="w-5 h-5 rounded-full border-2 border-gray-300 dark:border-secondary-600" />
                    )}
                    <span className="text-sm text-gray-600 dark:text-secondary-400">DKIM</span>
                  </div>
                </div>
              </div>

              {/* DNS Records Table */}
              {emailDomain.dnsRecords && (
                <div>
                  <h3 className="text-sm font-medium text-gray-700 dark:text-secondary-300 mb-3">
                    Add these DNS records to your domain:
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-secondary-700 border border-gray-200 dark:border-secondary-700 rounded-lg">
                      <thead className="bg-gray-50 dark:bg-secondary-800">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-secondary-400 uppercase tracking-wider">
                            Type
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-secondary-400 uppercase tracking-wider">
                            Name
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-secondary-400 uppercase tracking-wider">
                            Value
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-secondary-400 uppercase tracking-wider">
                            Purpose
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-secondary-400 uppercase tracking-wider w-12">

                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-secondary-900 divide-y divide-gray-200 dark:divide-secondary-700">
                        {[
                          ...emailDomain.dnsRecords.verification,
                          ...emailDomain.dnsRecords.mailFrom,
                          ...emailDomain.dnsRecords.dkim,
                        ].map((record: DnsRecord, index: number) => (
                          <tr key={index}>
                            <td className="px-4 py-3 whitespace-nowrap text-sm font-mono text-gray-900 dark:text-secondary-100">
                              {record.type}
                            </td>
                            <td className="px-4 py-3 text-sm font-mono text-gray-900 dark:text-secondary-100 break-all max-w-xs">
                              {record.name}
                            </td>
                            <td className="px-4 py-3 text-sm font-mono text-gray-900 dark:text-secondary-100 break-all max-w-md">
                              {record.value}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-secondary-400">
                              {record.purpose}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => copyToClipboard(record.value)}
                                title="Copy value"
                              >
                                <ClipboardDocumentIcon className="w-4 h-4" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="mt-3 text-sm text-gray-500 dark:text-secondary-400">
                    DNS changes can take up to 48 hours to propagate. Click "Refresh Status" to check verification progress.
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-secondary-300 mb-2">
                  Domain Name
                </label>
                <div className="flex gap-3">
                  <Input
                    value={domainInput}
                    onChange={(e) => setDomainInput(e.target.value)}
                    placeholder="example.com"
                    className="flex-1"
                  />
                  <Button
                    onClick={handleSaveEmailDomain}
                    isLoading={isEmailDomainSaving}
                    disabled={!domainInput.trim()}
                  >
                    Save Domain
                  </Button>
                </div>
                <p className="mt-2 text-sm text-gray-500 dark:text-secondary-400">
                  Enter the domain you want to send emails from. After saving, you'll need to add DNS records to verify ownership.
                </p>
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Integration Credentials Section */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-secondary-100 mb-4">Integration Credentials</h2>
        <div className="space-y-4">
          {/* Authorize.net */}
          <IntegrationCard
            title="Authorize.net"
            description="Payment processing gateway"
            icon={CreditCardIcon}
            isConfigured={configurations?.integrations.authNet.isConfigured || false}
            onSave={authNetForm.handleSubmit((data) => handleSaveCredentials('authNet', data))}
            isSaving={savingProvider === 'authNet'}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="API Login ID"
                {...authNetForm.register('apiLoginId')}
                error={authNetForm.formState.errors.apiLoginId?.message}
                placeholder="Enter API Login ID"
              />
              <Input
                label="Client Key"
                type={showPasswords.authNetClientKey ? 'text' : 'password'}
                {...authNetForm.register('clientKey')}
                error={authNetForm.formState.errors.clientKey?.message}
                placeholder="Enter Client Key"
                rightIcon={
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('authNetClientKey')}
                    className="text-gray-400 hover:text-gray-600 dark:text-secondary-500 dark:hover:text-secondary-300"
                  >
                    {showPasswords.authNetClientKey ? (
                      <EyeSlashIcon className="w-4 h-4" />
                    ) : (
                      <EyeIcon className="w-4 h-4" />
                    )}
                  </button>
                }
              />
              <Input
                label="Transaction Key"
                type={showPasswords.authNetTransactionKey ? 'text' : 'password'}
                {...authNetForm.register('transactionKey')}
                error={authNetForm.formState.errors.transactionKey?.message}
                placeholder="Enter Transaction Key"
                rightIcon={
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('authNetTransactionKey')}
                    className="text-gray-400 hover:text-gray-600 dark:text-secondary-500 dark:hover:text-secondary-300"
                  >
                    {showPasswords.authNetTransactionKey ? (
                      <EyeSlashIcon className="w-4 h-4" />
                    ) : (
                      <EyeIcon className="w-4 h-4" />
                    )}
                  </button>
                }
              />
              <Input
                label="Signature Key (Optional)"
                type={showPasswords.authNetSignatureKey ? 'text' : 'password'}
                {...authNetForm.register('signatureKey')}
                placeholder="Enter Signature Key"
                rightIcon={
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('authNetSignatureKey')}
                    className="text-gray-400 hover:text-gray-600 dark:text-secondary-500 dark:hover:text-secondary-300"
                  >
                    {showPasswords.authNetSignatureKey ? (
                      <EyeSlashIcon className="w-4 h-4" />
                    ) : (
                      <EyeIcon className="w-4 h-4" />
                    )}
                  </button>
                }
              />
            </div>

            {/* Webhook URL Section */}
            {authNetWebhookUrl && (
              <div className="mt-6 pt-6 border-t border-gray-200 dark:border-secondary-700">
                <label className="block text-sm font-medium text-gray-700 dark:text-secondary-300 mb-2">
                  Webhook URL
                </label>
                <p className="text-sm text-gray-500 dark:text-secondary-400 mb-3">
                  Add this webhook URL to your Authorize.net merchant dashboard to receive payment notifications.
                </p>
                <div className="flex items-center gap-2">
                  <Input
                    value={authNetWebhookUrl}
                    readOnly
                    className="font-mono text-sm bg-gray-50"
                  />
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => copyToClipboard(authNetWebhookUrl)}
                    type="button"
                  >
                    <ClipboardDocumentIcon className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </IntegrationCard>

          {/* MD Integrations */}
          <IntegrationCard
            title="MD Integrations"
            description="Telemedicine provider integration"
            icon={BuildingOfficeIcon}
            isConfigured={configurations?.integrations.mdi.isConfigured || false}
            onSave={mdiForm.handleSubmit((data) => handleSaveCredentials('mdi', data))}
            isSaving={savingProvider === 'mdi'}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Client ID"
                {...mdiForm.register('clientId')}
                error={mdiForm.formState.errors.clientId?.message}
                placeholder="Enter Client ID"
              />
              <Input
                label="Client Secret"
                type={showPasswords.mdiClientSecret ? 'text' : 'password'}
                {...mdiForm.register('clientSecret')}
                error={mdiForm.formState.errors.clientSecret?.message}
                placeholder="Enter Client Secret"
                rightIcon={
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('mdiClientSecret')}
                    className="text-gray-400 hover:text-gray-600 dark:text-secondary-500 dark:hover:text-secondary-300"
                  >
                    {showPasswords.mdiClientSecret ? (
                      <EyeSlashIcon className="w-4 h-4" />
                    ) : (
                      <EyeIcon className="w-4 h-4" />
                    )}
                  </button>
                }
              />
            </div>
          </IntegrationCard>

          {/* Curexa */}
          <IntegrationCard
            title="Curexa"
            description="Pharmacy fulfillment integration"
            icon={TruckIcon}
            isConfigured={configurations?.integrations.curexa.isConfigured || false}
            onSave={curexaForm.handleSubmit((data) => handleSaveCredentials('curexa', data))}
            isSaving={savingProvider === 'curexa'}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Client Key"
                {...curexaForm.register('clientKey')}
                error={curexaForm.formState.errors.clientKey?.message}
                placeholder="Enter Client Key"
              />
              <Input
                label="Client Secret"
                type={showPasswords.curexaClientSecret ? 'text' : 'password'}
                {...curexaForm.register('clientSecret')}
                error={curexaForm.formState.errors.clientSecret?.message}
                placeholder="Enter Client Secret"
                rightIcon={
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('curexaClientSecret')}
                    className="text-gray-400 hover:text-gray-600 dark:text-secondary-500 dark:hover:text-secondary-300"
                  >
                    {showPasswords.curexaClientSecret ? (
                      <EyeSlashIcon className="w-4 h-4" />
                    ) : (
                      <EyeIcon className="w-4 h-4" />
                    )}
                  </button>
                }
              />
            </div>
          </IntegrationCard>

          {/* PCP Health */}
          <IntegrationCard
            title="PCP Health"
            description="Pharmacy fulfillment integration (optional)"
            icon={TruckIcon}
            isConfigured={configurations?.integrations.pcpHealth?.isConfigured || false}
            onSave={pcpHealthForm.handleSubmit((data) => handleSaveCredentials('pcpHealth', data))}
            isSaving={savingProvider === 'pcpHealth'}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Account ID"
                {...pcpHealthForm.register('accountId')}
                error={pcpHealthForm.formState.errors.accountId?.message}
                placeholder="Enter Account ID"
              />
              <Input
                label="Secret"
                type={showPasswords.pcpHealthSecret ? 'text' : 'password'}
                {...pcpHealthForm.register('secret')}
                error={pcpHealthForm.formState.errors.secret?.message}
                placeholder="Enter Secret"
                rightIcon={
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('pcpHealthSecret')}
                    className="text-gray-400 hover:text-gray-600 dark:text-secondary-500 dark:hover:text-secondary-300"
                  >
                    {showPasswords.pcpHealthSecret ? (
                      <EyeSlashIcon className="w-4 h-4" />
                    ) : (
                      <EyeIcon className="w-4 h-4" />
                    )}
                  </button>
                }
              />
              <Input
                label="Sub-Account ID (optional)"
                {...pcpHealthForm.register('subAccountId')}
                error={pcpHealthForm.formState.errors.subAccountId?.message}
                placeholder="Enter Sub-Account ID"
              />
            </div>
          </IntegrationCard>

          {/* Google Analytics */}
          <IntegrationCard
            title="Google Analytics"
            description="Optional browser tracking and server-side event analytics"
            icon={ChartBarIcon}
            isConfigured={isGoogleAnalyticsServerSideConfigured(configurations?.integrations.googleAnalytics)}
            onSave={gaForm.handleSubmit((data) => handleSaveCredentials('googleAnalytics', data))}
            isSaving={savingProvider === 'googleAnalytics'}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Measurement ID"
                {...gaForm.register('measurementId')}
                placeholder="G-XXXXXXXXXX"
              />
              <Input
                label="API Secret"
                type={showPasswords.gaApiSecret ? 'text' : 'password'}
                {...gaForm.register('apiSecret')}
                placeholder="Enter API Secret"
                rightIcon={
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('gaApiSecret')}
                    className="text-gray-400 hover:text-gray-600 dark:text-secondary-500 dark:hover:text-secondary-300"
                  >
                    {showPasswords.gaApiSecret ? (
                      <EyeSlashIcon className="w-4 h-4" />
                    ) : (
                      <EyeIcon className="w-4 h-4" />
                    )}
                  </button>
                }
              />
            </div>
            <p className="mt-3 text-xs text-gray-500 dark:text-secondary-400">
              Measurement ID enables browser tracking. Server-side events require both the Measurement ID and API Secret.
              Google Analytics is optional. Leave either field empty to clear that credential.
            </p>
          </IntegrationCard>
        </div>
      </div>

      {/* Confirm Dialogs */}
      <ConfirmDialog
        isOpen={showRotateConfirm}
        onClose={() => setShowRotateConfirm(false)}
        onConfirm={handleRotateApiKey}
        title="Rotate API Key"
        message="Are you sure you want to rotate your API key? Your current key will be invalidated immediately and any integrations using it will stop working."
        confirmLabel="Rotate Key"
        variant="primary"
        isLoading={isApiActionLoading}
      />

      <ConfirmDialog
        isOpen={showRevokeConfirm}
        onClose={() => setShowRevokeConfirm(false)}
        onConfirm={handleRevokeApiAccess}
        title="Revoke API Access"
        message="Are you sure you want to revoke API access? All integrations using your API key will stop working immediately."
        confirmLabel="Revoke Access"
        variant="danger"
        isLoading={isApiActionLoading}
      />

      <ConfirmDialog
        isOpen={showDeleteDomainConfirm}
        onClose={() => setShowDeleteDomainConfirm(false)}
        onConfirm={handleDeleteEmailDomain}
        title="Remove Email Domain"
        message="Are you sure you want to remove this email domain configuration? Emails will be sent from the default Capture Health address."
        confirmLabel="Remove Domain"
        variant="danger"
        isLoading={isEmailDomainLoading}
      />
    </div>
  );
}

